import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  ButtonVariants,
  Divider,
  DropdownItem,
  DropdownList,
  FormControlSizes,
  HStack,
  Icon,
  ICON_SIZES,
  IconButton,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Popover,
  Text,
  useDisclosure,
  useMixpanel,
  usePopoverState,
} from '@talos/kyoko';
import { useCallback, useMemo, type CSSProperties, type MouseEvent } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useUser } from '../../hooks/useUser';
import { useAppLayoutConfig } from '../../providers/AppLayoutConfig/AppLayoutConfigContext';
import { useSpacesConfig } from '../../providers/AppLayoutConfig/SpaceConfigContext';
import type { NavigationItem } from '../../tokens/navigationPaths';
import type { LayoutSection } from '../../types/LayoutConfig';
import { DEFAULT_LAYOUT_ID } from '../AppLayout/Layouts/tokens';
import { useLayoutSpecsQuery, type LayoutSpecResponse } from '../AppLayout/LayoutSpecs/LayoutSpecSlice';
import { getLayoutSpecForNavItem } from './spaceLayoutLookup';
import { RemoveSpaceDialog } from './Spaces/RemoveSpaceDialog';
import { SidebarSubMenuNavItemRowStatic, SidebarSubMenuNavItemWrapper } from './styles';

const TARGET_STYLE: CSSProperties = {
  width: '100%',
  display: 'block',
};

const ICON_BUTTON_STYLE: CSSProperties = {
  margin: '-4px',
};

const DEVICE_TYPE_ICONS = {
  mobile: IconName.DeviceMobile,
  tablet: IconName.DeviceTablet,
  laptop: IconName.DeviceLaptop,
  desktop: IconName.DeviceDesktop,
  widescreen: IconName.DeviceDesktopWide,
  all: IconName.Globe,
};

export function SidebarSubMenuNavItem({
  item,
  layoutSection,
  rearrangeModeActive = false,
  onStartRearranging,
  onOpenEditSpace,
  onOpenManageTemplate,
  onRemoveDialogOpenChange,
}: {
  item: NavigationItem;
  layoutSection: LayoutSection | undefined;
  /** When true, sidebar is in rearrange mode: rows render as static blocks (not links); editable rows are draggable. */
  rearrangeModeActive?: boolean;
  onStartRearranging?: () => void;
  onOpenEditSpace?: (item: NavigationItem) => void;
  onOpenManageTemplate?: (item: NavigationItem, selectedTemplateConfig?: LayoutSpecResponse) => void;
  onRemoveDialogOpenChange?: (isOpen: boolean) => void;
}) {
  const mixpanel = useMixpanel();
  const { removeSpace, spaces } = useSpacesConfig();
  const navigate = useNavigate();
  const newPath = replaceLastPathSegment(item.path, DEFAULT_LAYOUT_ID);
  // Needed to to check if the item has any children that use prefix matching, so that the parents are highlighted correctly.
  const hasAnyChild = item.children?.some(child => {
    const navChild = child as NavigationItem;
    return navChild.path?.endsWith('/*') && navChild.label == null;
  });
  const match = useMatch(
    item.path
      ? {
          path: item.path,
          end: !hasAnyChild,
        }
      : ''
  );
  const isActive = !!match;
  const user = useUser();
  const { layoutConfigs } = useAppLayoutConfig(layoutSection);
  const { data: sharedLayoutSpecs } = useLayoutSpecsQuery();
  const layoutSpec = getLayoutSpecForNavItem({ item, sectionLayoutConfigs: layoutConfigs });

  const isRowDraggable = rearrangeModeActive && !!item.isEditable;
  const isOnlySpaceInSection = layoutSection != null && (spaces[layoutSection]?.length ?? 0) <= 1;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id ?? item.path ?? '',
    disabled: !isRowDraggable,
  });

  const sortableStyle: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Hide source row while dragging — DragOverlay in NavigationSidebar shows the preview.
    opacity: rearrangeModeActive && isDragging ? 0 : 1,
    position: 'relative',
    zIndex: isDragging ? 1 : undefined,
  };

  // Find the template config if this space was created from an editable template
  const editableTemplateConfig = useMemo((): LayoutSpecResponse | undefined => {
    if (!layoutSpec?.templateId || !sharedLayoutSpecs) {
      return undefined;
    }
    // Find the template in the shared specs that matches the templateId and is owned by the current user
    return sharedLayoutSpecs.find(spec => spec.Config.id === layoutSpec.templateId && spec.User === user.Name);
  }, [layoutSpec?.templateId, sharedLayoutSpecs, user.Name]);

  const canEditSpace = layoutSpec != null;
  const canRemoveSpace =
    item.id != null && item.id !== DEFAULT_LAYOUT_ID && layoutSpec != null && !isOnlySpaceInSection;

  const popover = usePopoverState({
    placement: 'right-start',
    trigger: '',
    usePortal: true,
    noPaddingAndBorder: true,
    closeOnClickOutside: true,
  });

  const removeDialog = useDisclosure({
    onOpen: () => onRemoveDialogOpenChange?.(true),
    onClose: () => onRemoveDialogOpenChange?.(false),
  });

  const handleOpenDropdown = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    popover.open();
  };

  const handleOpenCreateTemplateDialog = useCallback(() => {
    popover.close();
    mixpanel.track(MixpanelEvent.OpenCreateTemplateModal, {
      section: layoutSection,
      source: MixpanelEventSource.NavigationSidebarSubNavItem,
    });
    onOpenManageTemplate?.(item, undefined);
  }, [popover, mixpanel, layoutSection, onOpenManageTemplate, item]);

  const handleOpenEditTemplateDialog = useCallback(() => {
    popover.close();
    mixpanel.track(MixpanelEvent.OpenEditTemplateModal, {
      section: layoutSection,
      source: MixpanelEventSource.NavigationSidebarSubNavItem,
    });
    onOpenManageTemplate?.(item, editableTemplateConfig);
  }, [popover, mixpanel, layoutSection, onOpenManageTemplate, item, editableTemplateConfig]);

  const handleOpenEditSpaceDialog = useCallback(() => {
    if (!canEditSpace) {
      return;
    }
    popover.close();
    mixpanel.track(MixpanelEvent.OpenEditSpaceModal, {
      section: layoutSection,
      source: MixpanelEventSource.NavigationSidebarSubNavItem,
    });
    onOpenEditSpace?.(item);
  }, [canEditSpace, popover, mixpanel, layoutSection, onOpenEditSpace, item]);

  const handleRemove = useCallback(() => {
    if (!item.id) {
      return;
    }
    removeSpace(item.id);
    mixpanel.track(MixpanelEvent.RemoveSpace, {
      [MixpanelEventProperty.LayoutSection]: layoutSection,
      [MixpanelEventProperty.DeviceType]: item.deviceType ?? 'all',
    });
    if (isActive && newPath) {
      navigate(newPath);
    }
  }, [item.id, item.deviceType, removeSpace, mixpanel, layoutSection, isActive, newPath, navigate]);

  const handleRearrange = useCallback(() => {
    popover.close();
    onStartRearranging?.();
  }, [popover, onStartRearranging]);

  const rowInner = (
    <>
      <Box flex="1 1 0" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
        {item.label}
      </Box>
      <Actions>
        {item.deviceType && item.deviceType !== 'all' && (
          <Box px="spacing4">
            <Icon icon={DEVICE_TYPE_ICONS[item.deviceType]} />
          </Box>
        )}
        {item.isEditable && isRowDraggable && (
          <DragHandle data-testid="drag-handle" {...attributes} {...listeners}>
            <Icon icon={IconName.DragVertical} color="colorTextImportant" size={ICON_SIZES.MEDIUM} />
          </DragHandle>
        )}
        {item.isEditable && !isRowDraggable && (
          <IconButton
            ghost
            onClick={handleOpenDropdown}
            style={ICON_BUTTON_STYLE}
            icon={IconName.DotsVertical}
            size={FormControlSizes.Small}
          />
        )}
      </Actions>
    </>
  );

  return (
    <div ref={setNodeRef} style={sortableStyle}>
      <Popover {...popover} targetStyle={TARGET_STYLE}>
        {rearrangeModeActive ? (
          <SidebarSubMenuNavItemRowStatic
            key={item.path}
            data-testid="sidebar-submenu-nav-item"
            className={isActive ? 'active' : undefined}
          >
            {rowInner}
          </SidebarSubMenuNavItemRowStatic>
        ) : (
          <SidebarSubMenuNavItemWrapper
            key={item.path}
            data-testid="sidebar-submenu-nav-item"
            to={item.path || ''}
            onClick={() => {
              if (item.mixpanelEvent) {
                mixpanel.track(MixpanelEvent.NavigateTo, {
                  path: item.path,
                  source: MixpanelEventSource.NavigationSidebarSubNavItem,
                });
                mixpanel.track(item.mixpanelEvent);
              }
            }}
          >
            {rowInner}
          </SidebarSubMenuNavItemWrapper>
        )}
        {item.isEditable && !rearrangeModeActive && (
          <EditableItemMenu
            layoutSection={layoutSection}
            editableTemplateConfig={editableTemplateConfig}
            canEditSpace={canEditSpace}
            canRemoveSpace={canRemoveSpace}
            onOpenEditDialog={handleOpenEditSpaceDialog}
            onOpenRemoveDialog={removeDialog.open}
            onOpenCreateTemplateDialog={handleOpenCreateTemplateDialog}
            onOpenEditTemplateDialog={handleOpenEditTemplateDialog}
            onRearrange={handleRearrange}
          />
        )}
      </Popover>

      <RemoveSpaceDialog
        key={`remove_${removeDialog.isOpen.toString()}`}
        label={item.label}
        onConfirm={handleRemove}
        {...removeDialog}
      />
    </div>
  );
}

interface EditableItemMenuProps {
  layoutSection: LayoutSection | undefined;
  editableTemplateConfig: LayoutSpecResponse | undefined;
  canEditSpace: boolean;
  canRemoveSpace: boolean;
  onOpenEditDialog: () => void;
  onOpenRemoveDialog: () => void;
  onOpenCreateTemplateDialog: () => void;
  onOpenEditTemplateDialog: () => void;
  onRearrange: () => void;
}

function EditableItemMenu({
  layoutSection,
  editableTemplateConfig,
  canEditSpace,
  canRemoveSpace,
  onOpenEditDialog,
  onOpenRemoveDialog,
  onOpenCreateTemplateDialog,
  onOpenEditTemplateDialog,
  onRearrange,
}: EditableItemMenuProps) {
  return (
    <DropdownList data-sidebar-portal-interaction>
      {layoutSection && (
        <DropdownItem ghost startIcon={IconName.Share} onClick={onOpenCreateTemplateDialog}>
          <Text>Share</Text>
        </DropdownItem>
      )}
      {editableTemplateConfig && (
        <DropdownItem ghost startIcon={IconName.Template} onClick={onOpenEditTemplateDialog}>
          <Text>Edit Template</Text>
        </DropdownItem>
      )}
      <DropdownItem ghost startIcon={IconName.Pencil} disabled={!canEditSpace} onClick={onOpenEditDialog}>
        <Text>Edit Space</Text>
      </DropdownItem>
      <DropdownItem ghost startIcon={IconName.Reorder} onClick={onRearrange}>
        <Text>Rearrange</Text>
      </DropdownItem>
      <Divider />
      <DropdownItem
        ghost
        startIcon={IconName.Trash}
        disabled={!canRemoveSpace}
        variant={ButtonVariants.Negative}
        onClick={onOpenRemoveDialog}
      >
        <Text>Remove</Text>
      </DropdownItem>
    </DropdownList>
  );
}

function replaceLastPathSegment(path: string | undefined, replacement: string): string | undefined {
  if (!path) {
    return undefined;
  }
  return path.split('/').slice(0, -1).concat(replacement).join('/');
}

const Actions = styled(HStack)`
  gap: ${({ theme }) => theme.spacing4}px;
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  margin: -4px;
  padding: 4px;
  color: ${({ theme }) => theme.colorTextSubtle};

  &:active {
    cursor: grabbing;
  }
`;
